/**
 * Design Memory — constraint derivation.
 *
 * Turns a similarity report into actionable guidance. The Thinking Engine
 * consumes ONLY `generationConstraints` (what to avoid + which axes to vary) —
 * it never receives stored fingerprints or prior designs, only these derived
 * constraints, keeping previous templates invisible to it.
 */

import {
  SIMILARITY_DIMENSIONS, DIMENSION_FIELD, DEFAULT_THRESHOLDS,
} from './memoryVocabulary';

/**
 * @param {Object} report  from computeSimilarityReport.
 * @param {Object} [opts]
 * @param {number} [opts.highDimension]
 * @param {number} [opts.variationAxis]
 * @returns {Object} { diversityConstraints, generationConstraints, avoidRules }
 */
export const deriveConstraints = (report, opts = {}) => {
  const {
    highDimension = DEFAULT_THRESHOLDS.highDimension,
    variationAxis = DEFAULT_THRESHOLDS.variationAxis,
  } = opts;

  const perDim = report.perDimensionMax || {};
  const nearestFp = report.nearest && report.nearest.fingerprint;

  const tooSimilarDimensions = SIMILARITY_DIMENSIONS.filter((d) => (perDim[d] || 0) >= highDimension);

  const recommendedVariationAxes = SIMILARITY_DIMENSIONS
    .filter((d) => (perDim[d] || 0) >= variationAxis)
    .sort((a, b) => (perDim[b] || 0) - (perDim[a] || 0));

  const avoid = {};
  const avoidRules = [];
  if (nearestFp) {
    tooSimilarDimensions.forEach((dim) => {
      const value = nearestFp[DIMENSION_FIELD[dim]];
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return;
      avoid[dim] = value;
      // Scalar values name something concrete to avoid; list values mean "vary".
      avoidRules.push(Array.isArray(value) ? `vary:${dim}` : `avoid:${dim}:${value}`);
    });
  }

  return {
    diversityConstraints: {
      duplicate: Boolean(report.duplicate),
      maxSimilarity: report.maxSimilarity || 0,
      threshold: report.threshold,
      tooSimilarDimensions,
    },
    generationConstraints: { avoid, recommendedVariationAxes },
    avoidRules,
  };
};
